/**
 * Swiper for collection-list row (replaces theme scroll-carousel).
 * See featured-collections-swiper.js for Swiper 11 option notes.
 */
(function () {
  const SWIPER_SELECTOR = "[data-collection-list-swiper]";
  const HOST_SELECTOR = "[data-collection-list-swiper-host]";

  function readNumber(value, fallback) {
    if (value === undefined || value === null || value === "") {
      return fallback;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }

  function readPositiveNumber(value, fallback) {
    const n = readNumber(value, NaN);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  }

  function parseOptions(el) {
    const d = el.dataset;
    return {
      speed: readPositiveNumber(d.clSwiperSpeed, 400),
      loop: d.clSwiperLoop === "true",
      centeredSlides: d.clSwiperCentered === "true",
      autoplayDelay: Math.max(0, readNumber(d.clSwiperAutoplayDelay, 0)),
      spaceBetweenMobile: Math.max(0, readNumber(d.clSwiperSpaceMobile, 24)),
      spaceBetweenDesktop: Math.max(0, readNumber(d.clSwiperSpaceDesktop, 24)),
      slidesPerViewMobile: readPositiveNumber(d.clSwiperSpvMobile, 1.5),
      slidesPerViewDesktop: readPositiveNumber(d.clSwiperSpvDesktop, 4),
      pagination: d.clSwiperPagination === "true",
    };
  }

  function getSwiperConstructor() {
    const g = typeof globalThis !== "undefined" ? globalThis : window;
    return typeof g.Swiper === "function" ? g.Swiper : null;
  }

  function isRtl(el) {
    if (el?.getAttribute("dir") === "rtl") {
      return true;
    }
  }

  function progressScopeEl(swiperEl) {
    return (
      swiperEl.closest(".collection-list-swiper-scope") ||
      swiperEl.parentElement
    );
  }

  function updateProgressBar(swiper, scopeEl) {
    const bar = scopeEl?.querySelector(".collection-list-swiper__progress-bar");
    if (!bar) {
      return;
    }
    const total = swiper.slides?.length ?? 0;
    if (total <= 0) {
      return;
    }
    const raw = typeof swiper.progress === "number" ? swiper.progress : 0;
    const p = total === 1 ? 1 : raw * ((total - 1) / total) + 1 / total;
    bar.style.transform = `scaleX(${Math.min(1, Math.max(0, p))})`;
  }

  function createSwiper(el) {
    const SwiperCtor = getSwiperConstructor();
    if (!SwiperCtor || el.swiper) {
      return el.swiper ?? null;
    }

    const host = el.closest(HOST_SELECTOR);
    const prevBtn = host?.querySelector("[data-cl-swiper-prev] button");
    const nextBtn = host?.querySelector("[data-cl-swiper-next] button");
    const paginationEl = el.querySelector(
      ".collection-list-swiper__pagination",
    );

    const o = parseOptions(el);

    const autoplay =
      o.autoplayDelay > 0
        ? {
            delay: o.autoplayDelay,
            disableOnInteraction: true,
            pauseOnMouseEnter: true,
          }
        : false;

    const navigation =
      prevBtn && nextBtn
        ? {
            prevEl: prevBtn,
            nextEl: nextBtn,
          }
        : false;

    const pagination =
      o.pagination && paginationEl
        ? {
            el: paginationEl,
            clickable: true,
          }
        : false;

    const progressScope = progressScopeEl(el);

    try {
      return new SwiperCtor(el, {
        rtl: isRtl(el),
        speed: o.speed,
        loop: o.loop,
        centeredSlides: o.centeredSlides,
        slidesPerView: o.slidesPerViewMobile,
        spaceBetween: o.spaceBetweenMobile,
        watchOverflow: false,
        watchSlidesProgress: true,
        forceToAxis: true,
        simulateTouch: true,
        grabCursor: true,
        observer: false,
        observeParents: false,
        resizeObserver: true,
        navigation,
        pagination,
        autoplay,
        breakpoints: {
          700: {
            slidesPerView: o.slidesPerViewDesktop,
            spaceBetween: o.spaceBetweenDesktop,
          },
        },
        on: {
          init(s) {
            updateProgressBar(s, progressScope);
          },
          progress(s) {
            updateProgressBar(s, progressScope);
          },
          slideChange(s) {
            updateProgressBar(s, progressScope);
          },
        },
      });
    } catch (err) {
      console.warn("[collection-list] Swiper init failed:", err);
      return null;
    }
  }

  function initSwiper(el) {
    function run() {
      if (!getSwiperConstructor()) {
        return false;
      }
      requestAnimationFrame(() => {
        const swiper = createSwiper(el);
        swiper?.update();
        swiper?.navigation?.update?.();
        if (swiper) {
          updateProgressBar(swiper, progressScopeEl(el));
        }
      });
      return true;
    }

    if (run()) {
      return;
    }

    let tries = 0;
    const id = window.setInterval(() => {
      tries += 1;
      if (run() || tries > 60) {
        window.clearInterval(id);
      }
    }, 50);
  }

  function boot(root = document) {
    root.querySelectorAll(SWIPER_SELECTOR).forEach((el) => {
      if (!el.swiper && el.closest(".shopify-section")) {
        initSwiper(el);
      }
    });
  }

  function destroyInRoot(root) {
    root.querySelectorAll(SWIPER_SELECTOR).forEach((el) => {
      if (el.swiper && typeof el.swiper.destroy === "function") {
        el.swiper.destroy(true, true);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot(document));
  } else {
    boot(document);
  }

  window.addEventListener("load", () => boot(document));

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target?.querySelector?.(SWIPER_SELECTOR)) {
      boot(event.target);
    }
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroyInRoot(event.target);
  });

  function refreshSwipersInPanel(panel) {
    if (!panel?.querySelector) {
      return;
    }

    panel.querySelectorAll(SWIPER_SELECTOR).forEach((el) => {
      if (el.swiper) {
        el.swiper.update();
        el.swiper.navigation?.update?.();
        updateProgressBar(el.swiper, progressScopeEl(el));
      } else if (el.closest(".shopify-section")) {
        initSwiper(el);
      }
    });
  }

  document.addEventListener("block-tab:show", (event) => {
    refreshSwipersInPanel(event.target);
  });

  document.addEventListener("collection-list-lulu-tab:show", (event) => {
    refreshSwipersInPanel(event.target);
  });
})();
